import React, { useState, useEffect } from 'react'
import { NotificationTemplate } from '@shared/types/notificationTemplate'
import { useNotificationTemplates } from '@/hooks/useNotificationTemplates'
import { X, Eye, FileText } from 'lucide-react'

interface TemplatePreviewModalProps {
  isOpen: boolean
  onClose: () => void
  template: NotificationTemplate
}

export const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  isOpen,
  onClose,
  template
}) => {
  const { previewTemplate: doPreviewTemplate, preview, templateMetadata } = useNotificationTemplates()
  const [viewMode, setViewMode] = useState<'html' | 'text'>('html')
  const [sampleData, setSampleData] = useState<Record<string, any>>({})

  useEffect(() => {
    // Generate sample data based on template variables
    if (template && templateMetadata) {
      const metadata = templateMetadata[template.name]
      if (metadata) {
        const samples: Record<string, any> = {}
        metadata.availableVariables.forEach(variable => {
          samples[variable.name] = variable.example
        })
        setSampleData(samples)
      }
    }
  }, [template, templateMetadata])

  useEffect(() => {
    // Generate preview when sample data changes
    if (template && Object.keys(sampleData).length > 0) {
      doPreviewTemplate({
        subject: template.subject,
        bodyHtml: template.bodyHtml,
        bodyText: template.bodyText,
        variables: sampleData
      })
    }
  }, [template, sampleData])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Eye className="h-6 w-6" />
              Template Preview
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {template.name} - Version {template.version}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* View Mode Toggle */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('html')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'html'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              HTML Preview
            </button>
            <button
              onClick={() => setViewMode('text')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'text'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              Plain Text
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="p-6">
          {preview ? (
            <div className="space-y-4">
              {/* Subject Line */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Subject Line
                </label>
                <div className="p-3 bg-muted rounded-lg border border-border">
                  <p className="text-foreground font-medium">{preview.subject}</p>
                </div>
              </div>

              {/* Email Body */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Email Body
                </label>
                {viewMode === 'html' ? (
                  <div className="border border-border rounded-lg overflow-hidden">
                    <div
                      className="p-6 bg-background"
                      dangerouslySetInnerHTML={{ __html: preview.bodyHtml }}
                    />
                  </div>
                ) : (
                  <div className="p-6 bg-muted rounded-lg border border-border">
                    <pre className="whitespace-pre-wrap font-sans text-sm text-foreground">
                      {preview.bodyText}
                    </pre>
                  </div>
                )}
              </div>

              {/* Sample Data Used */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Sample Data Used
                </label>
                <div className="p-4 bg-muted rounded-lg border border-border">
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(sampleData).map(([key, value]) => (
                      <div key={key} className="flex flex-col">
                        <span className="text-xs text-muted-foreground font-mono">
                          {`{{${key}}}`}
                        </span>
                        <span className="text-sm text-foreground">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              Loading preview...
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
